import { Column, ColumnDef } from "@tanstack/react-table";
import { isWithinInterval, parseISO } from "date-fns";
import {
  Checkbox,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Badge,
} from "@pushpress/pushpress-ui";
import StatusBadge from "../shared/StatusBadge";
import ColumnSortHeaderButton from "../shared/ColumnSortHeaderButton";
import SubscriptionPopover from "../shared/SubscriptionPopover";
import SendMessageModal from "./SendMessageModal";
import ColumnTableHeader from "./ColumnTableHeader";
import MobileNameCellContent from "../shared/MobileNameCellContent";
import { memberChurnDateFilterOptions } from "../../constants";
import {
  LeadRowActions,
  MemberRowActions,
  StaffRowActions,
} from "./RowActions";
import {
  getDateFormat,
  getDateRangeForValue,
  getStatusFormat,
} from "../../helpers";
import {
  LeadRecordType,
  MemberRecordType,
  PeopleType,
  PEOPLE_TYPE,
  StaffRecordType,
} from "../../types";

const tableCellClassName =
  "tw-truncate tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-600";

const checkboxColumnItem = () => ({
  id: "select",
  header: ({ table }) => {
    const isAllRowsSelected = table.getIsAllRowsSelected();
    const isAllPageRowsSelected = table.getIsAllPageRowsSelected();
    return (
      <div className="tw-pl-2 tw-mr-4">
        <Checkbox
          checked={isAllPageRowsSelected || isAllRowsSelected}
          onCheckedChange={(value) =>
            isAllRowsSelected
              ? table.toggleAllRowsSelected(false)
              : table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
        />
      </div>
    );
  },
  cell: ({ row }) => (
    <div className="tw-pl-2">
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        data-testid="checkbox"
      />
    </div>
  ),
  enableSorting: false,
});

const nameColumnItem = (peopleType: PeopleType) => ({
  accessorKey: "name",
  accessorFn: (row) => `${row.firstName} ${row.lastName}`,
  header: ({ column, table }) => {
    const rowsSelected = table.getSelectedRowModel().rows;
    const hasRowsSelected = rowsSelected.length > 0;
    return (
      <div className="tw-h-[53px] tw-w-[150px] sm:tw-w-[330px] tw-flex tw-items-center">
        <div>
          {!hasRowsSelected ? (
            <ColumnSortHeaderButton
              column={column}
              title="Name"
              hasRowsSelected={hasRowsSelected}
            />
          ) : (
            <div className="tw-flex tw-w-full">
              <span className="tw-text-sm tw-font-semibold tw-text-neutral-800 max-sm:tw-text-xs tw-whitespace-nowrap tw-my-auto">
                {table.getSelectedRowModel().rows.length} selected
              </span>
              <SendMessageModal
                rowsSelected={rowsSelected}
                selectAllRows={() => table.toggleAllRowsSelected()}
                isAllRowsSelected={table.getIsAllRowsSelected()}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
  cell: ({ row }) => {
    return (
      <div className="tw-py-2.5 tw-w-[150px] sm:tw-w-[320px]">
        <div className="tw-flex tw-items-center">
          <Avatar className="tw-h-10 tw-w-10 tw-text-primary">
            <AvatarImage src={row.original.imageUrl} alt="image" />
            <AvatarFallback>
              {row.original.firstName.slice(0, 1).toUpperCase()}
              {row.original.lastName.slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="tw-ml-2">
            <div className="tw-font-medium tw-text-neutral-800 tw-break-words">
              {row.original.firstName} {row.original.lastName}
            </div>
            <div className="tw-mt-0.5 tw-text-neutral-600 tw-break-words max-sm:tw-hidden">
              {row.original.email}
            </div>
            <MobileNameCellContent row={row} peopleType={peopleType} />
          </div>
        </div>
      </div>
    );
  },
  filterFn: (row, id, value) => {
    // Filter by both name and email
    const searchValue = value.toLowerCase();
    const name =
      `${row.original.firstName} ${row.original.lastName}`.toLowerCase();
    const email = row.original.email?.toLowerCase() || "";

    return name.includes(searchValue) || email.includes(searchValue);
  },
});

const planColumnItem = () => ({
  accessorKey: "plan",
  accessorFn: (row) => row.subscriptions[0]?.name || "--",
  header: ({ column, table }) => {
    const hasRowsSelected = table.getFilteredSelectedRowModel().rows.length > 0;
    return (
      <ColumnTableHeader
        hasRowsSelected={hasRowsSelected}
        title="Plan"
        column={column}
        width="250px"
      />
    );
  },
  cell: ({ row }) => {
    return (
      <div
        className={`tw-w-[250px] tw-break-words max-sm:tw-hidden ${tableCellClassName}`}
        data-testid="plan-name"
      >
        {row.original.subscriptions?.[0]?.name || "--"}
        {row.original.subscriptions?.length > 1 && (
          <SubscriptionPopover
            label={row.original.subscriptions?.length - 1}
            subscriptions={row.original.subscriptions.slice(1, 10)}
          />
        )}
      </div>
    );
  },
  filterFn: (row, id, value) => {
    return value.includes(row.getValue(id));
  },
});

const lastCheckinColumnItem = () => ({
  accessorKey: "lastCheckin",
  header: ({ column, table }) => {
    const hasRowsSelected = table.getFilteredSelectedRowModel().rows.length > 0;
    return (
      <ColumnTableHeader
        hasRowsSelected={hasRowsSelected}
        title="Last check-in"
        column={column}
        width="130px"
      />
    );
  },
  cell: ({ row }) => {
    return (
      <div className={`tw-w-[130px] max-sm:tw-hidden ${tableCellClassName}`}>
        {getDateFormat(row.getValue("lastCheckin"))}
      </div>
    );
  },
});

const membershipRoleColumnItem = (peopleType: PeopleType) => ({
  accessorKey: "membershipRole",
  header: ({ column, table }) => {
    const hasRowsSelected = table.getFilteredSelectedRowModel().rows.length > 0;
    return (
      <ColumnTableHeader
        hasRowsSelected={hasRowsSelected}
        title={peopleType === PEOPLE_TYPE.MEMBERS ? "Type" : "Role"}
        column={column}
        width="150px"
      />
    );
  },
  cell: ({ row }) => {
    return (
      <div className={`tw-w-[150px] max-sm:tw-hidden ${tableCellClassName}`}>
        <Badge variant="neutral">
          {getStatusFormat(row.getValue("membershipRole"))}
        </Badge>
      </div>
    );
  },
  filterFn: (row, id, value) => {
    return value.includes(row.getValue(id));
  },
});

const rowActionsColumnItem = (peopleType: PeopleType) => ({
  id: "actions",
  accessorKey: "actions",
  header: null,
  cell: ({ row }) =>
    peopleType === PEOPLE_TYPE.STAFF ? (
      <StaffRowActions row={row} />
    ) : peopleType === PEOPLE_TYPE.LEADS ? (
      <LeadRowActions row={row} />
    ) : (
      <MemberRowActions row={row} />
    ),
});

export const membersColumns: ColumnDef<MemberRecordType>[] = [
  checkboxColumnItem(),
  nameColumnItem(PEOPLE_TYPE.MEMBERS),
  {
    accessorKey: "churnDate",
    header: null,
    cell: null,
    filterFn: (row, id, value) => {
      if (!row.original.churnDate) return false;
      const dateRange = getDateRangeForValue(value[0]);
      try {
        const churnDate = parseISO(row.original.churnDate);
        return isWithinInterval(churnDate, {
          start: dateRange.start,
          end: dateRange.end,
        });
      } catch (error) {
        return false;
      }
    },
    meta: {
      getChurnDateFilterFacets: (rows: MemberRecordType[]) => {
        const facetCounts = new Map(
          memberChurnDateFilterOptions.map((option) => [option.value, 0]),
        );
        rows.forEach((row) => {
          if (row.churnDate) {
            try {
              const churnDate = parseISO(row.churnDate);
              facetCounts.forEach((count, rangeKey) => {
                const dateRange = getDateRangeForValue(rangeKey);

                if (
                  isWithinInterval(churnDate, {
                    start: dateRange.start,
                    end: dateRange.end,
                  })
                ) {
                  facetCounts.set(rangeKey, count + 1);
                }
              });
            } catch (error) {
              // Skip this row if date parsing fails
            }
          }
        });
        return facetCounts;
      },
    },
  },
  membershipRoleColumnItem(PEOPLE_TYPE.MEMBERS),
  planColumnItem(),
  {
    accessorKey: "status",
    accessorFn: (row) => row.subscriptions?.[0]?.status || "--",
    header: ({ column, table }) => {
      const hasRowsSelected =
        table.getFilteredSelectedRowModel().rows.length > 0;
      return (
        <ColumnTableHeader
          hasRowsSelected={hasRowsSelected}
          title="Status"
          column={
            column as Column<
              MemberRecordType | LeadRecordType | StaffRecordType
            >
          }
          width="120px"
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className={`tw-w-[150px] max-sm:tw-hidden ${tableCellClassName}`}>
          <StatusBadge
            peopleType={PEOPLE_TYPE.MEMBERS}
            status={row.original.subscriptions[0].status}
          />
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  lastCheckinColumnItem(),
  rowActionsColumnItem(PEOPLE_TYPE.MEMBERS),
];

export const leadsColumns: ColumnDef<LeadRecordType>[] = [
  checkboxColumnItem(),
  nameColumnItem(PEOPLE_TYPE.LEADS),
  {
    accessorKey: "leadStatus",
    header: ({ column, table }) => {
      const hasRowsSelected =
        table.getFilteredSelectedRowModel().rows.length > 0;
      return (
        <ColumnTableHeader
          hasRowsSelected={hasRowsSelected}
          title="Status"
          column={
            column as Column<
              MemberRecordType | LeadRecordType | StaffRecordType
            >
          }
          width="120px"
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className={`tw-w-[120px] max-sm:tw-hidden ${tableCellClassName}`}>
          <StatusBadge
            peopleType={PEOPLE_TYPE.LEADS}
            status={row.getValue("leadStatus")}
          />
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "firstContacted",
    header: ({ column, table }) => {
      const hasRowsSelected =
        table.getFilteredSelectedRowModel().rows.length > 0;
      return (
        <ColumnTableHeader
          hasRowsSelected={hasRowsSelected}
          title="First contacted"
          column={
            column as Column<
              MemberRecordType | LeadRecordType | StaffRecordType
            >
          }
          width="150px"
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className={`tw-w-[150px] max-sm:tw-hidden ${tableCellClassName}`}>
          {getDateFormat(row.getValue("firstContacted"))}
        </div>
      );
    },
  },
  planColumnItem(),
  lastCheckinColumnItem(),
  rowActionsColumnItem(PEOPLE_TYPE.LEADS),
];

export const staffColumns: ColumnDef<StaffRecordType>[] = [
  checkboxColumnItem(),
  nameColumnItem(PEOPLE_TYPE.STAFF),
  membershipRoleColumnItem(PEOPLE_TYPE.STAFF),
  lastCheckinColumnItem(),
  rowActionsColumnItem(PEOPLE_TYPE.STAFF),
];
